import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import {
  Image,
  Layout,
  Palette,
  Flag,
  Droplet,
  FileText,
  // Hash, // Hidden for now - numbering tab
  Languages,
  FileCheck,
  CreditCard,
  ShoppingCart,
  RotateCcw,
  Eye,
  Save,
  Check,
  Menu,
  X,
  PanelLeftClose,
  PanelLeft,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TemplateDesignerProvider, useTemplateDesigner } from './TemplateDesignerContext';
import { DEFAULT_TEMPLATE_SETTINGS } from './types';
import { useApp } from '@/components/AppLayout';
import { templateConfigApi } from '@/lib/api';
import { generateBannerHtml, getIconSvgFromUrl } from './utils';
import { LogoPanel } from './panels/LogoPanel';
import { TemplatePanel } from './panels/TemplatePanel';
import { ColorPanel } from './panels/ColorPanel';
import { BannerPanel } from './panels/BannerPanel';
import { WatermarkPanel } from './panels/WatermarkPanel';
import { DocumentOptionsPanel } from './panels/DocumentOptionsPanel';
import { LabelsPanel } from './panels/LabelsPanel';
import { DocumentPreview } from './DocumentPreview';
import type { DocumentType, BusinessData } from './types';
import { SAMPLE_BUSINESS } from './sampleData';

const SIDEBAR_SECTIONS = [
  { id: 'logo', icon: Image, label: 'Logo' },
  { id: 'template', icon: Layout, label: 'Template' },
  { id: 'color', icon: Palette, label: 'Colors' },
  { id: 'banner', icon: Flag, label: 'Banner' },
  { id: 'watermark', icon: Droplet, label: 'Watermark' },
  { id: 'document', icon: FileText, label: 'Document' },
  // { id: 'numbering', icon: Hash, label: 'Numbering' }, // Hidden for now
  { id: 'labels', icon: Languages, label: 'Labels' },
] as const;

const DOCUMENT_TYPES: { id: DocumentType; icon: typeof FileText; label: string }[] = [
  { id: 'invoice', icon: FileText, label: 'Invoice' },
  { id: 'quote', icon: FileCheck, label: 'Quote' },
  { id: 'credit_memo', icon: CreditCard, label: 'Credit Memo' },
  { id: 'purchase_order', icon: ShoppingCart, label: 'PO' },
];

// Simple two-breakpoint system:
// < 1024px: Overlay sidebar + collapsible panel
// >= 1024px: Fixed sidebar + side panel

function TemplateDesignerContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, user } = useApp();
  const {
    activeSection,
    setActiveSection,
    previewDocumentType,
    setPreviewDocumentType,
    resetSettings,
    settings,
    loadSettings,
    isLoading,
    setIsLoading,
  } = useTemplateDesigner();

  // Panel state (works at all sizes)
  const [panelOpen, setPanelOpen] = useState(true);
  // Sidebar overlay state (for < 1024px)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const previewRef = useRef<HTMLDivElement>(null);

  // Build business data: use real data where available, sample data as fallback
  const businessData: BusinessData = useMemo(() => ({
    id: currentBusiness?.id || SAMPLE_BUSINESS.id,
    name: currentBusiness?.name || SAMPLE_BUSINESS.name,
    address: currentBusiness?.address || SAMPLE_BUSINESS.address,
    industry: currentBusiness?.industry || SAMPLE_BUSINESS.industry,
    logo: currentBusiness?.logo || SAMPLE_BUSINESS.logo,
    // Phone not available on Business type - use sample
    phone: SAMPLE_BUSINESS.phone,
    // Use user's email if available
    email: user?.email || SAMPLE_BUSINESS.email,
  }), [currentBusiness, user]);

  // Load saved settings on mount
  useEffect(() => {
    if (!currentBusiness) {
      setIsLoading(false);
      return;
    }

    templateConfigApi.get(currentBusiness.id)
      .then(response => {
        if (response.data?.settings) {
          // Deep merge with defaults to ensure all nested fields exist
          const saved = response.data.settings as Partial<import('./types').TemplateSettings>;
          const merged: import('./types').TemplateSettings = {
            ...DEFAULT_TEMPLATE_SETTINGS,
            ...saved,
            // Deep merge nested objects
            logo: { ...DEFAULT_TEMPLATE_SETTINGS.logo, ...saved.logo },
            colorTheme: { ...DEFAULT_TEMPLATE_SETTINGS.colorTheme, ...saved.colorTheme },
            banner: { ...DEFAULT_TEMPLATE_SETTINGS.banner, ...saved.banner },
            watermark: { ...DEFAULT_TEMPLATE_SETTINGS.watermark, ...saved.watermark },
            footer: { ...DEFAULT_TEMPLATE_SETTINGS.footer, ...saved.footer },
            labels: { ...DEFAULT_TEMPLATE_SETTINGS.labels, ...saved.labels },
          };
          loadSettings(merged);
        }
      })
      .catch(() => {
        // No saved config, use defaults - this is fine
      })
      .finally(() => setIsLoading(false));
  }, [currentBusiness, loadSettings, setIsLoading]);

  // Save template to backend
  const saveTemplate = useCallback(async () => {
    if (!currentBusiness) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      // Generate banner HTML from style and colors for Owl Ops
      const bannerHtml = settings.banner.enabled
        ? generateBannerHtml(settings.banner.style, settings.colorTheme, settings.banner.height)
        : '';

      // Generate icon SVGs for preset icons (except initials, which are generated at render time)
      const businessName = currentBusiness?.name || '';
      // Don't pre-generate initials SVG - it should be generated at render time to use current business name
      const logoIconSvg = settings.logo.url === 'icon:initials'
        ? null
        : getIconSvgFromUrl(settings.logo.url, businessName);
      const watermarkIconSvg = settings.watermark.content?.startsWith('icon:') && settings.watermark.content !== 'icon:initials'
        ? getIconSvgFromUrl(settings.watermark.content, businessName)
        : null;

      // Build settings with generated HTML/SVG for Owl Ops
      const settingsForSave = {
        ...settings,
        banner: {
          ...settings.banner,
          html: bannerHtml,
        },
        logo: {
          ...settings.logo,
          iconSvg: logoIconSvg,
        },
        watermark: {
          ...settings.watermark,
          iconSvg: watermarkIconSvg,
        },
      };

      await templateConfigApi.save(currentBusiness.id, {
        template_name: settings.selectedLayout,
        settings: settingsForSave as unknown as Record<string, unknown>,
      });
      setSaveSuccess(true);
      // Reset success state after 2 seconds
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentBusiness, settings]);

  // Close sidebar overlay on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderPanel = () => {
    switch (activeSection) {
      case 'logo': return <LogoPanel />;
      case 'template': return <TemplatePanel />;
      case 'color': return <ColorPanel />;
      case 'banner': return <BannerPanel />;
      case 'watermark': return <WatermarkPanel />;
      case 'document': return <DocumentOptionsPanel />;
      case 'labels': return <LabelsPanel />;
      default: return <LogoPanel />;
    }
  };

  const currentSection = SIDEBAR_SECTIONS.find(s => s.id === activeSection);

  // Handle section selection
  const handleSectionSelect = (sectionId: string) => {
    setActiveSection(sectionId);
    setPanelOpen(true);
    // Close sidebar overlay on non-desktop
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Show loading state while fetching saved settings
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading template settings...</span>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 overflow-hidden">

        {/* Sidebar Overlay Backdrop (< 1024px) */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Left Sidebar */}
        {/* Desktop: always visible, relative */}
        {/* < 1024px: fixed overlay, slides in */}
        <div
          className={`
            lg:relative lg:translate-x-0 lg:w-48
            fixed z-50 h-full w-56
            flex flex-col bg-white dark:bg-slate-900 border-r border-border
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* Header */}
          <div className="h-[60px] px-3 border-b border-border flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground gap-2"
              onClick={() => {
                setSidebarOpen(false);
                nav('/settings');
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Exit</span>
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-muted-foreground"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Navigation Items */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {SIDEBAR_SECTIONS.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleSectionSelect(section.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      transition-colors duration-150
                      ${isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>

          {/* Bottom Actions */}
          <div className="p-2 border-t border-border">
            <button
              onClick={resetSettings}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <RotateCcw className="w-4 h-4 flex-shrink-0" />
              <span>Reset All</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col lg:flex-row min-w-0">

          {/* Settings Panel */}
          <motion.div
            initial={false}
            animate={{ width: panelOpen ? 320 : 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-slate-900 border-r border-border overflow-hidden flex-col flex-shrink-0 hidden lg:flex"
          >
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 h-[60px] border-b border-border w-[320px]">
              <div className="flex items-center gap-3">
                {currentSection && (
                  <>
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <currentSection.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-sm">{currentSection.label}</h2>
                      <p className="text-xs text-muted-foreground">Customize settings</p>
                    </div>
                  </>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setPanelOpen(false)}
                className="text-muted-foreground"
              >
                <PanelLeftClose className="w-4 h-4" />
              </Button>
            </div>

            {/* Panel Content */}
            <ScrollArea className="flex-1">
              <div className="p-4 w-[320px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    {renderPanel()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </ScrollArea>
          </motion.div>

          {/* Collapsed Panel Toggle - Desktop only */}
          {!panelOpen && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => setPanelOpen(true)}
              className="hidden lg:flex absolute left-48 top-1/2 -translate-y-1/2 z-10 w-6 h-12 bg-white dark:bg-slate-900 border border-border rounded-r-lg shadow-md items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <PanelLeft className="w-4 h-4" />
            </motion.button>
          )}

          {/* Preview Area */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-2 sm:px-3 h-[60px] bg-white dark:bg-slate-900 border-b border-border gap-2 flex-shrink-0">
              {/* Menu Button (< 1024px) */}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-muted-foreground flex-shrink-0"
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Live Preview Label */}
              <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Live Preview</span>
              </div>

              {/* Document Type Selector - icons only until xl breakpoint (1280px) to account for panel */}
              <div className="flex items-center gap-0.5 p-0.5 bg-muted rounded-lg flex-shrink-0">
                {DOCUMENT_TYPES.map((doc) => {
                  const Icon = doc.icon;
                  const isActive = previewDocumentType === doc.id;
                  return (
                    <Tooltip key={doc.id}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setPreviewDocumentType(doc.id)}
                          className={`
                            px-1.5 sm:px-2 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5
                            transition-all duration-200
                            ${isActive
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                            }
                          `}
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="hidden xl:inline">{doc.label}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{doc.label}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>

              {/* Save Button */}
              <Button
                size="sm"
                onClick={saveTemplate}
                disabled={isSaving || !currentBusiness}
                className="text-xs sm:text-sm flex-shrink-0"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" />
                ) : saveSuccess ? (
                  <Check className="w-4 h-4 sm:mr-1.5" />
                ) : (
                  <Save className="w-4 h-4 sm:mr-1.5" />
                )}
                <span className="hidden sm:inline">
                  {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Template'}
                </span>
              </Button>
            </div>

            {/* Content Area - Desktop: side by side, Mobile: stacked with panel below */}
            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
              {/* Preview Content */}
              <div
                ref={previewRef}
                className={`
                  p-2 sm:p-4 lg:p-6 flex items-start justify-center bg-slate-100 dark:bg-slate-800
                  lg:flex-1 lg:overflow-auto
                  ${panelOpen ? 'flex-1 min-h-0 overflow-auto' : 'flex-1 overflow-auto'}
                `}
              >
                <div
                  className="w-full transition-all duration-200"
                  style={{
                    maxWidth: '816px',
                    transform: 'scale(var(--preview-scale, 1))',
                    transformOrigin: 'top center'
                  }}
                >
                  <DocumentPreview businessData={businessData} />
                </div>
              </div>

              {/* Non-Desktop Panel (< 1024px) - Shows below preview */}
              <AnimatePresence>
                {panelOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.2 }}
                    className="lg:hidden flex-shrink-0 bg-white dark:bg-slate-900 border-t-2 border-primary/20 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] overflow-hidden"
                    style={{ maxHeight: '45vh' }}
                  >
                    <div className="flex items-center justify-between p-3 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
                      <div className="flex items-center gap-2 min-w-0">
                        {currentSection && (
                          <>
                            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                              <currentSection.icon className="w-4 h-4" />
                            </div>
                            <span className="font-semibold text-sm truncate">{currentSection.label}</span>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setPanelOpen(false)}
                        className="text-muted-foreground hover:bg-primary/10 flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="p-4 overflow-y-auto overflow-x-hidden" style={{ maxHeight: 'calc(45vh - 52px)' }}>
                      {renderPanel()}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export function TemplateDesigner() {
  return (
    <TemplateDesignerProvider>
      <TemplateDesignerContent />
    </TemplateDesignerProvider>
  );
}
